import cx from 'classnames';
import type { ReactNode } from 'react';
import type { Except } from 'type-fest';
import { deprecate } from '../../utils/deprecate';
import type { IconProps } from '../Icon';
import type { DefaultLinkImplementationProps, LinkImplementationProps, LinkImplementationRequiredProps } from '../Link';
import { Link } from '../Link';
import { Typography } from '../Typography';
import styles from './styles.module.css';

interface IconButtonContentProps {
  icon: IconProps['name'];
  color?: 'black' | 'white';
  disabled?: boolean;
  /** @internal for close button only */
  fill?: boolean;
}

function IconButtonContent({ disabled, color, icon, fill = true }: IconButtonContentProps): ReactNode {
  return <Typography.Icon fill={fill} color={disabled ? 'black-light' : color} name={icon} />;
}

export interface IconButtonProps extends IconButtonContentProps {
  /** @internal for close button only */
  fill?: boolean;
  onClick?: () => void;
}

function iconButtonClassName({ color, disabled }: Partial<IconButtonProps>): string {
  return cx(styles.IconButton, {
    [styles.White]: color === 'white',
    [styles.Disabled]: disabled,
  });
}

/** @deprecated use IconButton from kitt-universal instead */
export function IconButton({ icon, color = 'black', disabled, fill, ...props }: IconButtonProps): ReactNode {
  if (__DEV__) deprecate(IconButton.name, 'Use IconButton from kitt-universal instead');
  const IconButtonContainer = props.onClick ? 'button' : 'div';

  return (
    <IconButtonContainer
      type="button"
      disabled={disabled}
      className={iconButtonClassName({ color, disabled })}
      onClick={props.onClick}
    >
      <IconButtonContent disabled={disabled} color={color} icon={icon} fill={fill} />
    </IconButtonContainer>
  );
}

export type IconButtonLinkProps<P extends LinkImplementationRequiredProps> = IconButtonContentProps &
  LinkImplementationProps<P>;

function IconButtonLink<P extends LinkImplementationRequiredProps = DefaultLinkImplementationProps>({
  disabled,
  color,
  icon,
  className,
  ...props
}: IconButtonLinkProps<P>): ReactNode {
  return (
    <Link {...props} className={cx(iconButtonClassName({ color, disabled }), className)}>
      <IconButtonContent disabled={disabled} color={color} icon={icon} />
    </Link>
  );
}
IconButton.Link = IconButtonLink;

export function CloseIconButton(props: Except<IconButtonProps, 'icon' | 'fill'>): ReactNode {
  return <IconButton {...props} icon="cross" fill={false} />;
}
